import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { fetchAllSupplementsShared } from "../store/api/medicationsApi";
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://vwwjda2e99.us-west-2.awsapprunner.com ';

export const DataContext = React.createContext();

// Catalogs refresh every 5 minutes while the page is in view; they rarely change. Coming
// back to the tab or window refreshes them too once the last refresh is this old.
export const CATALOG_REFRESH_MS = 5 * 60 * 1000;
export const CATALOG_REFRESH_ON_FOCUS_MS = 50 * 1000;

// Remembers each list's serialization, so the state side of a comparison is serialized
// only once however many refreshes compare against it.
const serializedLists = new WeakMap();
const serialize = (value) => {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (!serializedLists.has(value)) serializedLists.set(value, JSON.stringify(value));
  return serializedLists.get(value);
};

/** The current list when the fetched one holds the same data, else the fetched one. */
export const keepSameList = (prev, next) => {
  if (prev === next) return prev;
  if (Array.isArray(prev) && Array.isArray(next) && prev.length !== next.length) return next;
  try {
    return serialize(prev) === serialize(next) ? prev : next;
  } catch {
    return next;
  }
};

// State updater that changes only `key`, and keeps the whole state when that list's
// data didn't change (so a refresh with no changes re-renders nothing).
const withList = (key, list) => (prevData) => {
  const next = keepSameList(prevData[key], list);
  return next === prevData[key] && key in prevData ? prevData : { ...prevData, [key]: next };
};

export const Provider = ({ children }) => {
  const [isTyping, setIsTyping] = useState(false);
  const [data, setData] = useState({
    labs: [],
    dispensed: [],
    procedures: [],
  });

  const fetchLabs = useCallback(() => {
    axios
      .get(`${API_BASE_URL}/allLabs`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
          // Include any other headers as needed
        },
      })
      .then((response) => {
        setData(withList('labs', response.data.allLabs));
      })
      .catch((error) => {

      });
  }, []);
  // Always asks the server (this is the periodic refresh), but joins a request for the
  // same list that the medications panel already has on its way.
  const fetchSupplements = useCallback(() => {
    fetchAllSupplementsShared({ force: true })
      .then((response) => {
        setData(withList('supplements', response.data));
      })
      .catch((error) => {

      });
  }, []);
  const fetchDispensed = useCallback(() => {
    axios
      .get(`${API_BASE_URL}/allDispensed`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
          // Include any other headers as needed
        },
      })
      .then((response) => {
        setData(withList('dispensed', response.data.allDispensed));
      })
      .catch((error) => {
        console.error("There was an error fetching the dispenses", error);
      });
  }, []);
  const fetchProcedures = useCallback(() => {
    axios
      .get(`${API_BASE_URL}/allProcedures`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
          // Include any other headers as needed
        },
      })
      .then((response) => {
        setData(withList('procedures', response.data.allProcedures));
      })
      .catch((error) => {
        console.error("There was an error fetching the procedures", error);
      });
  }, []);

  useEffect(()=>{
    fetchLabs();
    fetchDispensed();
    fetchProcedures();
    fetchSupplements();
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Read through a ref so typing doesn't restart the refresh timer.
  const isTypingRef = useRef(isTyping);
  isTypingRef.current = isTyping;

  // Periodic refresh while the page is in view. A hidden tab skips it and catches up as
  // soon as it is shown again; returning to the tab or window also refreshes once the
  // last refresh is CATALOG_REFRESH_ON_FOCUS_MS old.
  useEffect(() => {
    let lastRefresh = Date.now();
    const refresh = () => {
      lastRefresh = Date.now();
      fetchLabs();
      fetchDispensed();
      fetchProcedures();
      fetchSupplements();
    };
    const interval = setInterval(() => {
      if (!isTypingRef.current && document.visibilityState === 'visible') refresh();
    }, CATALOG_REFRESH_MS);
    const onReturn = () => {
      if (
        document.visibilityState === 'visible' &&
        !isTypingRef.current &&
        Date.now() - lastRefresh >= CATALOG_REFRESH_ON_FOCUS_MS
      ) {
        refresh();
      }
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, [fetchLabs, fetchDispensed, fetchProcedures, fetchSupplements]);

  const value = useMemo(
    () => ({ data, setData, isTyping, setIsTyping, fetchLabs, fetchDispensed, fetchProcedures, fetchSupplements }),
    [data, isTyping, fetchLabs, fetchDispensed, fetchProcedures, fetchSupplements]
  );

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
